/**
 * There are a couple of places in the UI where we don't just want a list of channels,
 * we really want that list of channels grouped by connection. Maybe the API could
 * return it that way but it's not clear to me whether everybody needs it structured
 * that way or not. Instead, this is just a little helper class that builds a ViewModel
 * for the UI to use. It also explicitly breaks out the org-wide connection to make it
 * easier to decide behavior patterns based on that.
 */
import {
  SlackChannelLinkDTO,
  SlackConnectionDTO,
  TeamsChannelLinkDTO,
  TeamsConnectionDTO,
} from "groopit-js";

export class TeamsConnectionLinks {
  connection: TeamsConnectionDTO;
  orgWideLink?: TeamsChannelLinkDTO;
  channelLinks: TeamsChannelLinkDTO[];

  constructor(link: TeamsChannelLinkDTO) {
    this.connection = link.connection;
    if (link.channelId) {
      this.channelLinks = [link];
    } else {
      this.orgWideLink = link;
      this.channelLinks = [];
    }
  }

  add = (link: TeamsChannelLinkDTO) => {
    if (link.channelId) {
      this.channelLinks.push(link);
    } else {
      this.orgWideLink = link;
    }
  };

  get = (id: number): TeamsChannelLinkDTO | undefined => {
    if (this.orgWideLink?.id === id) {
      return this.orgWideLink;
    } else {
      return this.channelLinks.find((l) => l.id === id);
    }
  };
}

export class SlackConnectionLinks {
  connection: SlackConnectionDTO;
  orgWideLink?: SlackChannelLinkDTO;
  channelLinks: SlackChannelLinkDTO[];

  constructor(link: SlackChannelLinkDTO) {
    this.connection = link.connection;
    if (link.channelId) {
      this.channelLinks = [link];
    } else {
      this.orgWideLink = link;
      this.channelLinks = [];
    }
  }

  add = (link: SlackChannelLinkDTO) => {
    if (link.channelId) {
      this.channelLinks.push(link);
    } else {
      this.orgWideLink = link;
    }
  };

  get = (id: number): SlackChannelLinkDTO | undefined => {
    if (this.orgWideLink?.id === id) {
      return this.orgWideLink;
    } else {
      return this.channelLinks.find((l) => l.id === id);
    }
  };
}

export const groupByConnection = (
  links: (SlackChannelLinkDTO | TeamsChannelLinkDTO)[],
): (SlackConnectionLinks | TeamsConnectionLinks)[] => {
  const groupedLinks = Array<SlackConnectionLinks | TeamsConnectionLinks>();
  links.forEach((link) => {
    const connection = groupedLinks.find(
      (g) => g.connection.id === link.connection.id,
    );
    if (!connection) {
      groupedLinks.push(
        link instanceof TeamsChannelLinkDTO
          ? new TeamsConnectionLinks(link)
          : new SlackConnectionLinks(link),
      );
    } else {
      if (link instanceof TeamsChannelLinkDTO) {
        (connection as TeamsConnectionLinks).add(link);
      } else {
        (connection as SlackConnectionLinks).add(link);
      }
    }
  });
  return groupedLinks;
};
